import axios from "axios";
import { PromiseHandler } from "../../../handlers";
import { parseIdFilter } from "../../../handlers/filter";
import { isServicePage } from "../../../handlers/page";
import { NotImplementedError } from "../../../errors";
import { validators } from "./validators";
import { pricingAgencyFromService, pricingAgencyBillingInfoFromService, upsertAgencyCpmRequestToService } from "../../../models/verticalBilling";
import { AgencyPricingFetchFailed, AgencyPricingSaveFailed } from "./errors";
import { toAgencyDetailQueryParams, toAgencyPricingQueryParams } from "../filters";
const REQUEST_CONFIG = { headers: { "Content-Type": "application/json" } };
/**
 * Agency-level retail CPMs for vertical contracts.
 * A single id filter returns the agency detail with its product rates;
 * any other find returns the paginated, searchable agency list.
 */
class PricingAgencies extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "pricing-agencies", { atomize: true });
        this.url = `${this.sdk.urls.baseAPIUrl}/vertical-contracts`;
        this.validators = validators;
    }
    async findItems(filters = {}, sort) {
        const idFilter = parseIdFilter(filters);
        try {
            return idFilter.size === 1
                ? await this.getAgencyItems(idFilter.values().next().value)
                : await this.getAgencyList(filters, sort);
        }
        catch (error) {
            throw new AgencyPricingFetchFailed(error);
        }
    }
    /**
     * @param filters - Search and pagination for the agency list.
     * @param sort - Optional sort configuration.
     * @return A promise resolving to agencies, or a Page of agencies when the response is paginated.
     */
    getAgencyList(filters, sort) {
        const url = `${this.url}/agencies${toAgencyPricingQueryParams(filters, sort)}`;
        const pageSize = filters.paging?.size || 50;
        // List rows have no billingInfo, so they must not replace cached details.
        return this.cache.promise(url, () => axios
            .get(url, REQUEST_CONFIG)
            .then(({ data: res }) => {
            const agencies = res.data?.map(pricingAgencyFromService) || [];
            if (isServicePage(res)) {
                return {
                    page: {
                        count: res.paging_info.count,
                        token: res.paging_info.token,
                        size: pageSize
                    },
                    data: agencies
                };
            }
            return agencies;
        }), { atomize: false });
    }
    /**
     * @param agencyId - The id of the agency to get.
     * @param filters - Optional filters for search and pagination of nested products.
     * @param sort - Optional sort configuration for nested products (name, cpm).
     * @return A promise resolving to agency with billing info and pagination info.
     */
    async getAgency(agencyId, filters, sort) {
        const url = `${this.detailUrl(agencyId)}${toAgencyDetailQueryParams(filters, sort)}`;
        return axios
            .get(url, REQUEST_CONFIG)
            .then(({ data: res }) => ({
            agency: pricingAgencyBillingInfoFromService(res.data),
            totalCount: res.paging_info?.count ?? 0,
            pageToken: res.paging_info?.token
        }))
            .catch((error) => {
            throw new AgencyPricingFetchFailed(error);
        });
    }
    /**
     * @param agencyId - The id of the agency to get.
     * @return A promise resolving to the agency, with billingInfo, as a single-item list.
     */
    getAgencyItems(agencyId) {
        const url = this.detailUrl(agencyId);
        return this.cache.promise(url, () => this.fetchAgency(agencyId).then((agency) => [agency]));
    }
    fetchAgency(agencyId) {
        return axios
            .get(this.detailUrl(agencyId), REQUEST_CONFIG)
            .then(({ data: res }) => pricingAgencyBillingInfoFromService(res.data));
    }
    detailUrl(agencyId) {
        return `${this.url}/agency/${agencyId}`;
    }
    /**
     * Validates and upserts the agency's CPMs, then refetches the detail since the endpoint returns no data.
     * billingInfo.products should hold only the changed rows; a row without a cpm deletes that rate.
     * @param agency - Agency with the changed product rows.
     * @return The refreshed agency with billingInfo.
     */
    async saveItem(agency, { skipValidation } = {}) {
        if (!skipValidation) {
            const errors = this.validate(agency);
            if (errors.size) {
                throw errors;
            }
        }
        const request = upsertAgencyCpmRequestToService(agency);
        if (!request.upserts.length) {
            throw new AgencyPricingSaveFailed(null, "No product CPMs to update.");
        }
        try {
            await axios.post(`${this.url}/agency-cpms`, request, REQUEST_CONFIG);
            return await this.fetchAgency(agency.id);
        }
        catch (error) {
            throw new AgencyPricingSaveFailed(error);
        }
    }
    async deleteItem() {
        throw new NotImplementedError("delete");
    }
}
export default PricingAgencies;
//# sourceMappingURL=index.js.map